// src/pages/Profile.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, getUserData, updateUserBackground, updateUserLanguage } from '../services/auth';
import type { UserDocument, UserBackground } from '../types/user';
import { useSubscription } from '../hooks/useSubscription';
import {
  User as UserIcon,
  ChevronRight,
  Lock,
  LogOut,
  Globe,
  Languages,
  X,
  MessageCircle,
  Shield
} from 'lucide-react';
import TimeZoneSelect from '../components/common/TimeZoneSelect';
import SelectInput from '../components/common/SelectInput';
import Button from '../components/common/Button';
import AccordionPanel from '../components/common/AccordionPanel';
// import PersonalBackgroundForm from '../components/profile/PersonalBackgroundForm'; // 暫時不使用
import SubscriptionStatusCard from '../components/profile/SubscriptionStatusCard';
import { NotificationSettings } from '../components/profile/NotificationSettings';
import { PushNotificationTester } from '../components/profile/PushNotificationTester';
import { CalendarDays } from 'lucide-react';
import { useDebugMode } from '../hooks/useDebugMode';
// import { useAdminCheck } from '../hooks/useAdminCheck'; // 移除重複的調用，改用 checkAdminStatus
import { checkAdminStatus } from '../services/admin/adminService';
import { useOnboarding } from '../features/onboarding';

// 版本號全域變數類型聲明
declare global {
  interface Window {
    APP_VERSION?: string;
  }
}

// 帳號管理選項組件
const AccountManagementOptions: React.FC<{
  onChangePassword: () => void;
  onManageSubscription: () => void;
  onFeedback: () => void;
  onLogout: () => void;
  onAdmin?: () => void;
  showAdminOption?: boolean;
  onPreloadSubscription?: () => void;
}> = ({ onChangePassword, onManageSubscription, onFeedback, onLogout, onAdmin, showAdminOption, onPreloadSubscription }) => (
  <div className="space-y-3">
    {/* 修改密碼按鈕 */}
    <button
      onClick={onChangePassword}
      className="w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-center">
        <Lock size={20} className="text-brand-blue mr-3" />
        <span className="font-medium">Change Password</span>
      </div>
      <ChevronRight size={18} className="text-neutral-400" />
    </button>

    {/* 訂閱管理按鈕 */}
    <button
      onClick={onManageSubscription}
      onMouseEnter={onPreloadSubscription}
      className="w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-center">
        <Globe size={20} className="text-brand-blue mr-3" />
        <span className="font-medium">Subscription</span>
      </div>
      <ChevronRight size={18} className="text-neutral-400" />
    </button>

    {/* 回饋建議按鈕 */}
    <button
      onClick={onFeedback}
      className="w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-center">
        <MessageCircle size={20} className="text-brand-blue mr-3" />
        <span className="font-medium">Feedback</span>
      </div>
      <ChevronRight size={18} className="text-neutral-400" />
    </button>

    {/* 管理員面板按鈕 */}
    {showAdminOption && onAdmin && (
      <button
        onClick={onAdmin}
        className="w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center">
          <Shield size={20} className="text-brand-blue mr-3" />
          <span className="font-medium">Admin Panel</span>
        </div>
        <ChevronRight size={18} className="text-neutral-400" />
      </button>
    )}

    {/* 登出按鈕 */}
    <button
      onClick={onLogout}
      className="w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-center">
        <LogOut size={20} className="text-error-500 mr-3" />
        <span className="font-medium text-error-600">Sign Out</span>
      </div>
      <ChevronRight size={18} className="text-neutral-400" />
    </button>
  </div>
);

// 版本號點擊區域組件
const VersionClickArea: React.FC = () => {
  const { isDebugMode, failedAttempts, maxAttempts, isLocked, attemptEnableDebugMode, disableDebugMode } = useDebugMode();
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleVersionClick = () => {
    const now = Date.now();

    // 如果距離上次點擊超過 2 秒，重置計數
    if (now - lastClickTime > 2000) {
      setClickCount(1);
    } else {
      setClickCount(prev => prev + 1);
    }
    setLastClickTime(now);

    // 連按 10 下觸發
    if (clickCount >= 9) {
      if (isLocked) {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setShowPasswordModal(true);
      }
      setClickCount(0);
    }
  };

  const handlePasswordSubmit = () => {
    if (password.trim() === '') {
      setError('Please enter a password');
      return;
    }

    const success = attemptEnableDebugMode(password);
    if (success) {
      setShowPasswordModal(false);
      setPassword('');
      setError('');
    } else {
      setError(`Invalid password. Attempts: ${failedAttempts + 1}/${maxAttempts}`);
    }
  };

  const handleModalClose = () => {
    setShowPasswordModal(false);
    setPassword('');
    setError('');
  };

  return (
    <>
      <div className="flex items-center justify-center gap-2">
        <p
          onClick={handleVersionClick}
          className={`select-none cursor-pointer ${isDebugMode ? 'text-green-500' : ''}`}
        >
          Orlo version {(typeof window !== 'undefined' && window.APP_VERSION) || 'dev'} {isDebugMode && '(Debug Mode)'}
        </p>
        {isDebugMode && (
          <button
            onClick={disableDebugMode}
            className="text-gray-400 hover:text-red-500 text-sm transition-colors"
            title="關閉 Debug Mode"
          >
            ×
          </button>
        )}
      </div>

      {/* 密碼輸入模態框 */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-white rounded-lg p-6 w-80 max-w-sm mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Developer Mode</h3>
              <button
                onClick={handleModalClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter today's password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Enter password"
                  autoFocus
                />
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handlePasswordSubmit}
                  className="flex-1 bg-sky-600 text-white py-2 px-4 rounded-md hover:bg-sky-700 transition-colors"
                >
                  Enter
                </button>
                <button
                  onClick={handleModalClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Profile: React.FC = () => {
  const { currentUser, logoutUser } = useAuth();
  const { display, isLoading: subscriptionLoading } = useSubscription(currentUser);
  const { resetAllTips } = useOnboarding();
  const navigate = useNavigate();

  // 使用本地狀態管理管理員檢查，避免與 AdminRoute 重複
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [language, setLanguage] = useState<string>('');

  // 檢查管理員權限 (僅在組件載入時執行一次)
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      try {
        const status = await checkAdminStatus();
        setIsAdmin(status.isAdmin);
      } catch (error) {
        console.error('Profile: Failed to check admin status:', error);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser]);

  // 載入用戶完整資料
  useEffect(() => {
    if (currentUser) {
      setLoading(true);
      getUserData(currentUser.uid)
        .then(data => {
          setUserData(data as UserDocument | null);
          if (data?.displayName) {
            setDisplayName(data.displayName);
          }
        })
        .catch(err => {
          console.error('Error loading user data:', err);
          setError('Unable to load user data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [currentUser]);

  useEffect(() => {
    const loadLanguagePreference = async () => {
      if (!currentUser) return;

      try {
        // 優先順序：Firestore → localStorage → browser → 'en-US'
        let savedLanguage = '';

        // 1. 嘗試從 Firestore 讀取（如果用戶數據已載入）
        if (userData?.settings?.language) {
          savedLanguage = userData.settings.language;
        } else {
          // 2. 從 localStorage 讀取
          savedLanguage = localStorage.getItem('language') || '';
        }

        // 3. 如果都沒有，使用瀏覽器語言或預設值
        if (!savedLanguage) {
          const browserLang = navigator.language;
          const supportedLanguages = ['zh-TW', 'zh-CN', 'en-US', 'ja', 'ko', 'es', 'fr', 'de', 'ru', 'pt', 'it'];

          // 尋找匹配的語言代碼，優先完整匹配，再部分匹配
          savedLanguage = supportedLanguages.find(lang => lang === browserLang) ||
            supportedLanguages.find(lang => browserLang.startsWith(lang.split('-')[0])) ||
            'en-US';
        }

        setLanguage(savedLanguage);

        // 同步到 localStorage
        localStorage.setItem('language', savedLanguage);
      } catch (error) {
        console.error('載入語言偏好失敗:', error);
        // 發生錯誤時使用預設值
        setLanguage('en-US');
        localStorage.setItem('language', 'en-US');
      }
    };

    loadLanguagePreference();
  }, [currentUser, userData]); // 依賴 userData 變化

  // 處理語言變更（實作樂觀更新）
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value;
    const previousLanguage = language; // 保存舊值用於回滾

    if (!currentUser) {
      setError('No authenticated user found');
      return;
    }

    // 1. 樂觀更新 - 立即更新 UI
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    showSuccess('Language preference updated successfully');

    try {
      // 2. 背景同步到 Firestore
      await updateUserLanguage(currentUser.uid, newLanguage);
    } catch (error) {
      // 3. 失敗時回滾 UI 狀態
      console.error('同步語言偏好到 Firestore 失敗:', error);

      setLanguage(previousLanguage);
      localStorage.setItem('language', previousLanguage);

      if (error instanceof Error) {
        setError(`Failed to save language preference: ${error.message}`);
      } else {
        setError('Failed to save language preference. Changes have been reverted.');
      }
    }
  };

  // 顯示成功訊息
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
  };

  // 處理個人資料更新
  const handleUpdateProfile = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await updateUserProfile(currentUser.uid, {
        displayName: displayName.trim() || undefined
      });

      showSuccess('Profile updated successfully');
      setIsEditingName(false);

      // 重新獲取用戶資料以更新界面
      const updatedData = await getUserData(currentUser.uid);
      setUserData(updatedData as UserDocument | null);
    } catch (err) {
      console.error('Error updating profile:', err);
      if (err instanceof Error) {
        setError(`Update failed: ${err.message}`);
      } else {
        setError('Failed to update profile. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 處理背景資訊更新 (暫時未使用，保留供未來啟用 PersonalBackgroundForm 時使用)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateBackground = async (backgroundData: UserBackground) => {
    if (!currentUser) return false;

    setLoading(true);
    setError(null);

    try {
      await updateUserBackground(currentUser.uid, backgroundData);

      showSuccess('Background information updated successfully');

      // 重新獲取用戶資料以更新界面
      const updatedData = await getUserData(currentUser.uid);
      setUserData(updatedData as UserDocument | null);

      return true;
    } catch (err) {
      console.error('Error updating background:', err);
      if (err instanceof Error) {
        setError(`Update failed: ${err.message}`);
      } else {
        setError('Failed to update background information. Please try again later.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 處理登出
  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed. Please try again later.');
    }
  };

  // 處理時區更新成功
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTimeZoneChange = (newTimeZone: string) => {
    showSuccess('Timezone updated successfully');
  };

  // 導航到目標頁面
  const navigateToGoalPage = () => {
    try {
      navigate('/goals');
    } catch (err) {
      console.error('Navigation to goals page failed:', err);
      setError('Goals management page is not yet available');
    }
  };

  // 導航到密碼變更頁面
  const navigateToPasswordChange = () => {
    navigate('/profile/password-change');
  };

  // 導航到訂閱管理頁面 - 增加預載機制
  const navigateToSubscription = () => {
    try {
      // 觸發訂閱資料預載（如果尚未載入）
      // 將來實作預載邏輯

      navigate('/profile/subscription');
    } catch (err) {
      console.error('Navigation to subscription page failed:', err);
      setError('Subscription management page is not yet available');
    }
  };

  // 導航到回饋頁面
  const navigateToFeedback = () => {
    navigate('/feedback');
  };

  // 導航到管理員面板
  const navigateToAdmin = () => {
    navigate('/admin/feedback');
  };

  return (
    <div className="h-full flex flex-col content-loose">
      {/* 錯誤通知區域 */}
      {error && (
        <div className="px-4 py-3 bg-error-50 text-error-600 text-sm border-b border-error-100 animate-fadeIn">
          {error}
        </div>
      )}

      {/* 成功通知區域 */}
      {success && (
        <div className="px-4 py-3 bg-success-50 text-success-600 text-sm border-b border-success-100 animate-fadeIn">
          {successMessage || 'Profile updated'}
        </div>
      )}

      <div className="flex-1 space-y-4 overflow-auto hide-scrollbar">
        {/* === 用戶基本資料區 === */}
        <div className="flex flex-col items-center py-6 rounded-xl">
          {/* 用戶頭像 */}
          <div className="h-20 w-20 rounded-full bg-brand-blue flex items-center justify-center text-white mb-4">
            {currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt="Profile" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <UserIcon size={32} />
            )}
          </div>

          {/* 用戶名稱編輯區 */}
          {isEditingName ? (
            <div className="flex w-full max-w-xs">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="flex-1 px-3 py-2 text-center border border-neutral-200 rounded-l-lg focus:outline-none focus:ring-1 focus:ring-brand-blue"
                disabled={loading}
                placeholder="Your name"
                autoFocus
              />
              <Button
                onClick={handleUpdateProfile}
                disabled={loading}
                isLoading={loading}
                variant="primary"
                className="rounded-l-none"
              >
                Save
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-1">{displayName || 'Name not set'}</h2>
              <button
                onClick={() => setIsEditingName(true)}
                className="text-sm text-brand-blue hover:text-sky-600"
              >
                Edit name
              </button>
            </div>
          )}

          {/* 用戶郵箱 */}
          <p className="text-neutral-500 mt-2">{currentUser?.email}</p>

          {/* Quick Feedback Link */}
          <button
            onClick={navigateToFeedback}
            className="mt-4 px-4 py-2 bg-sky-50 hover:bg-sky-100 text-sky-600 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
          >
            <MessageCircle size={16} />
            <span>Send Feedback</span>
          </button>
        </div>

        {/* === 會員狀態卡片 === */}
        <SubscriptionStatusCard
          display={display}
          loading={subscriptionLoading}
          clickable={true}
          onPreloadSubscription={() => {
            // 將來實作 Hover 預載邏輯
          }}
        />

        {/* === My Goals 按鈕 === */}
        <button
          onClick={navigateToGoalPage}
          className="md:hidden w-full flex justify-between items-center px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
        >
          <span className="font-medium">My Goals</span>
          <ChevronRight size={18} className="text-neutral-400" />
        </button>

        {/* === Ideal Week 入口按鈕 === */}
        <button
          onClick={() => navigate('/profile/ideal-week')}
          className="w-full flex items-center justify-between px-4 py-4 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
          aria-label="Configure Ideal Week themes"
        >
          <div className="flex items-center">
            <CalendarDays size={18} className="text-sky-500 mr-3" />
            <span className="font-medium">Ideal Week</span>
          </div>
          <ChevronRight size={18} className="text-neutral-400" />
        </button>

        {/* === 個人背景資訊面板 - 考慮棄用，暫時隱藏 === */}
        {/*
        <AccordionPanel title="About You" defaultOpen={false}>
          {currentUser && (
            <PersonalBackgroundForm
              userId={currentUser.uid}
              initialData={userData?.background || {}}
              onSave={handleUpdateBackground}
            />
          )}
        </AccordionPanel>
        */}

        {/* === 系統偏好設定面板 === */}
        <AccordionPanel title="Preferences" defaultOpen={true}>
          <div className="space-y-4 p-4">
            {/* 時區設定 */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 flex items-center">
                <Globe size={18} className="text-brand-blue mr-2" />
                Timezone
              </label>

              {currentUser && (
                <TimeZoneSelect
                  userId={currentUser.uid}
                  className="w-full"
                  onChange={handleTimeZoneChange}
                />
              )}
            </div>

            {/* 語言設定 */}
            <div>
              <SelectInput
                label="Language"
                icon={<Languages size={18} className="text-brand-blue" />}
                value={language}
                onChange={handleLanguageChange}
              >
                <option value="en-US">English</option>
                <option value="zh-TW">繁體中文</option>
                <option value="zh-CN">简体中文</option>
                <option value="ja-JP">日本語</option>
                <option value="ko-KR">한국어</option>
                <option value="es-ES">Español</option>
                <option value="fr-FR">Français</option>
                <option value="de-DE">Deutsch</option>
                <option value="ru-RU">Русский</option>
                <option value="pt-PT">Português</option>
                <option value="it-IT">Italiano</option>
                <option value="vi-VN">Tiếng Việt</option>
              </SelectInput>
            </div>

            {/* Push Notifications 設定 */}
            <div>
              {isAdmin ? (
                // Admin 使用完整測試工具
                <PushNotificationTester />
              ) : (
                // 一般用戶使用基礎設定
                <NotificationSettings />
              )}
            </div>

            {/* Tips 重置按鈕（僅開發環境） */}
            {process.env.NODE_ENV === 'development' && (
              <div className="pt-4 border-t border-neutral-200">
                <button
                  onClick={() => {
                    resetAllTips();
                    showSuccess('All tips have been reset. Refresh to see them again.');
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  }}
                  className="w-full px-4 py-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg text-amber-700 font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <span>Reset All Tips</span>
                  <span className="text-xs text-amber-600">(Dev Only)</span>
                </button>
              </div>
            )}

          </div>
        </AccordionPanel>

        {/* === 帳號管理面板 === */}
        <AccordionPanel title="Account" defaultOpen={false}>
          <div className="p-4">
            <AccountManagementOptions
              onChangePassword={navigateToPasswordChange}
              onManageSubscription={navigateToSubscription}
              onFeedback={navigateToFeedback}
              onLogout={handleLogout}
              onAdmin={navigateToAdmin}
              showAdminOption={isAdmin}
              onPreloadSubscription={() => {
                // 將來實作 Hover 預載邏輯
              }}
            />
          </div>
        </AccordionPanel>

        {/* === 版權信息 === */}
        <div className="text-center text-neutral-400 text-sm pt-4 pb-8">
          <VersionClickArea />
          <p className="mt-1">© 2025 Orlo Life Management</p>
        </div>
      </div>
    </div>
  );
};

export default Profile;